import { Benefit, SEGMENTOS_POS } from "@/api/products";
import BasePlan from "@/components/BasePlan";
import Benefits from "@/components/Benefits";
import CompositionPlan from "@/components/CompostionPlan";
import ArrowDownIcon from "@/components/Icons/ArrowDown";
import ProductsDialog from "@/components/ProductsDialog";
import Switch from "@/components/Switch";
import SwitchItem from "@/components/SwitchItem";
import Text from "@/components/Text";
import useBreakpoint from "@/hooks/useBreakpoint";
import useUpdateEffect from "@/hooks/useUpdateEffect";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import { getOnlyNumbers, getPriceBaseByDDD } from "@/utils";
import { useState } from "react";

const sidebarStatus = {
  idle: "idle",
  open: "open",
};

const ProductsSidebar = () => {
  const product = useProductStore((store) => store.item);
  const clienteQuerDescontoExtra = useProductStore((store) => store.clienteQuerDescontoExtra());

  const [status, setStatus] = useState(sidebarStatus.idle);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const closeDialog = () => setIsDialogOpen(false);

  const loyalty = useProductStore((store) => store.loyalty);
  const offerAreaCode = useProductStore((store) => store.areaCode);
  const setLoyalty = useProductStore((store) => store.setLoyalty);

  const form = useFormStore((store) => store.value);
  const ddd = useFormStore((store) => store.value.tel).slice(0, 2);

  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );

  const temDescontoExtra = product?.beneficios.some(
    ({ componente } : Benefit) => componente === "DescontoExtra"
  ) && clienteQuerDescontoExtra;

  const isExpress = useProductStore((store) => store.isExpress);

  const price = String(currentPrice).replace(".", ",");
  const handleAsideClick = () => {
    setStatus((oldStatus) => {
      if (oldStatus === sidebarStatus.idle) {
        return sidebarStatus.open;
      }
      return sidebarStatus.idle;
    });
  };

  const { isOnBreakpoint } = useBreakpoint(1024);

  const isMobileOpen = isOnBreakpoint && status === sidebarStatus.open;
  const isDesktopOpen = !isOnBreakpoint;
  const isAsideOpen = isMobileOpen || isDesktopOpen;

  const isPos = product ? SEGMENTOS_POS.includes(product.segmento) : false;

  useUpdateEffect(() => {
    setLoyalty(true);
  }, [product?.tim_id]);

  const areaCode = ddd || offerAreaCode || undefined;
  const priceBase = getPriceBaseByDDD(product?.precos, areaCode);

  const classNameText = isExpress()
    ? "mt-4 text-tim-primary"
    : "text-tim-primary";
  const classNameArrowDown = isExpress()
    ? "ml-2 mt-4 text-tim-primary"
    : "ml-2 text-tim-primary";
  return (
    <>
      <ProductsDialog isOpen={isDialogOpen} onDismiss={closeDialog} />

      {isOnBreakpoint ? (
        <button
          type="button"
          className="bg-write flex h-12 w-full items-center justify-center focus:outline-none"
          onClick={handleAsideClick}
        >
          <Text as="h2" size="xl" className={classNameText}>
            <span className="mr-1 text-xs">até</span>
            <strong className="font-bold">{product?.gb}GB </strong>
            {`a partir de R$ ${price}/mês ${
              isExpress() ? "no cartão de crédito" : ""
            }${temDescontoExtra ? "com desconto extra" : ""}`}
          </Text>

          <span className={classNameArrowDown}>
            <ArrowDownIcon
              size={16}
              className={isAsideOpen ? "rotate-180 transform" : ""}
            />
          </span>
        </button>
      ) : null}
      {isAsideOpen && (
        <div className="mb-4 flex h-full flex-col text-tim-primary lg:rounded-2xl lg:bg-white lg:shadow-sm">
          <div className="px-3 pt-5 pb-4 leading-none lg:px-5">
            <div className="flex flex-col items-start justify-between space-y-3">
              {!isOnBreakpoint && (
                <Text as="h2" size="xl" className="pb-4 md:pb-0">
                  <span className="mr-1 text-xs">até</span>
                  <strong className="font-bold">{product?.gb}GB </strong>
                  {`a partir de R$ ${price}/mês ${
                    isExpress() ? "no cartão de crédito" : ""
                  }${temDescontoExtra ? "com desconto extra" : ""}`}
                </Text>
              )}
              {product?.fidelidades?.[0] && (
                <Switch
                  name="loyalty"
                  defaultValue={loyalty}
                  onChange={() => setLoyalty(!loyalty)}
                  key={product.tim_id}
                  type="primary"
                >
                  <SwitchItem type="primary" value={false} />
                  <SwitchItem type="primary" value>
                    <strong className="mb-1 block text-xl">
                      {product?.fidelidades[0].descricao_omega ||
                        product?.fidelidades[0].descricao}
                    </strong>
                    <span className="text-gray-500 ">
                      {`sem fidelidade fica R$ ${priceBase}/mês`}
                    </span>
                  </SwitchItem>
                </Switch>
              )}
              <BasePlan gbPlan={product?.gbPadrao || 0} />
              <CompositionPlan
                gbPadrao={product?.gbPadrao}
                gbPromocional={product?.gbPromocional}
                gbBonusPortabilidade={product?.gbBonusPortabilidade}
                mostrarPortabilidade={product?.mostrarPortabilidade}
                gbBonusDedicado={product?.gbBonusDedicated}
                mostrarDedicado={product?.mostrarDedicado}
                isPos={isPos}
              />
              {product?.beneficios && (
                <Benefits benefits={product.beneficios} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsSidebar;
